import { useState, useMemo, useRef, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import DashboardLayout from "../../layoutcontainers/DashboardLayout";
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Tooltip,
  Popover,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import MaterialTypography from "../../components/MaterialTypography";
import "./index.css";
import CalendarActions from "./components/calendarActions.js";
import BASEURL from "../Utils/Baseurl";
import { getUserAttendanceDetails } from "./components/calendarData.js";
import axios from "axios";
import { useDialog } from "../../context/dialogContext";
import moment from "moment";
import Swal from "sweetalert2";
import {
  EventAvailable,
  EventBusy,
  EventRepeat,
  Refresh,
  BeachAccessOutlined,
  WeekendOutlined,
  TimerOutlined,
  InfoOutlined,
  CheckCircleOutline,
  AccessTime,
  DeleteOutline,
} from "@mui/icons-material";
import MaterialBox from "../../components/MaterialBox";
import ApplyLeaveDialog from "./components/applyLeave/index.js";
import RegularizationForm from "./components/regularization/index.js";

// Helper to format text (e.g. sick_leave -> Sick Leave)
const formatText = (text) => {
  if (typeof text !== "string") return "";
  return text
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Calculate duration between startTime and endTime in seconds
const calculateDurationInSeconds = (startTime, endTime) => {
  if (!startTime || !endTime || endTime === "00:00:00") return 0;
  const parseTime = (t) => {
    if (typeof t !== "string") return null;
    const parts = t.split(":").map(Number);
    if (parts.some(isNaN)) return null;
    return parts[0] * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
  };
  const startSec = parseTime(startTime);
  const endSec = parseTime(endTime);
  if (startSec === null || endSec === null) return 0;
  const diffSec = endSec - startSec;
  return diffSec > 0 ? diffSec : 0;
};

// Format duration into readable string (e.g. "8 hrs 30 mins")
const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "0 hrs";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0 && minutes > 0) return `${hours} hrs ${minutes} mins`;
  if (hours > 0) return `${hours} hrs`;
  return `${minutes} mins`;
};

// Format time string to 12-hour format
const formatTimeString = (timeStr) => {
  if (!timeStr || timeStr === "00:00:00") return "N/A";
  try {
    return new Date(`1970-01-01T${timeStr}`).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return timeStr;
  }
};

const Calendar = () => {
  const user = sessionStorage.getItem("user");
  const userData = user ? JSON.parse(user) : {};
  const userId = userData?.id;
  const employeeId = userData?.empid;

  const [regularizationOpen, setRegularizationOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [inTime, setInTime] = useState("");
  const [calendarDataSet, setCalendarDataSet] = useState([]);
  const { dialogOpen, setDialogOpen } = useDialog();
  const [loading, setLoading] = useState(true);
  const [leaveId, setLeaveId] = useState("");
  const [viewScreen, setViewScreen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  // Month and Year state tracked dynamically from FullCalendar view
  const [currentMonth, setCurrentMonth] = useState(null);
  const [currentYear, setCurrentYear] = useState(null);
  const activeMonthRef = useRef({ month: null, year: null });
  const calendarRef = useRef(null);

  // Popover state for event description on click
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [selectedEventData, setSelectedEventData] = useState(null);



  // Fetch Attendance Details from API for a specific month and year
  const fetchUserAttendanceDetails = async (monthToFetch, yearToFetch) => {
    try {
      setLoading(true);
      const m = monthToFetch ?? activeMonthRef.current.month ?? (new Date().getMonth() + 1);
      const y = yearToFetch ?? activeMonthRef.current.year ?? new Date().getFullYear();

      activeMonthRef.current = { month: m, year: y };

      const response = await axios.post(
        BASEURL,
        {
          query: getUserAttendanceDetails,
          variables: {
            empid: employeeId,
            month: m,
            year: y,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const resUser = response.data?.data?.allUserMonthlyAttendance?.user;      
      const attendanceData = Array.isArray(resUser)
        ? resUser
        : resUser
          ? [resUser]
          : [];

      // Filter to ONLY the current logged-in employee to prevent events from other users leaking/repeating
      const currentEmployeeId = employeeId || userData?.empid || userData?.employeeId;
      const targetUsers = attendanceData.filter((u) => {
        if (!currentEmployeeId && !userId) return true;
        return (
          (currentEmployeeId && (u.empid === currentEmployeeId || u.userId === currentEmployeeId)) ||
          (userId && (u.userId === userId || u.empid === userId))
        );
      });

      const userRecordsToProcess =
        targetUsers.length > 0
          ? targetUsers
          : attendanceData.length === 1
            ? attendanceData
            : [];

      const parsedEvents = [];
      const todayStr = moment().format("YYYY-MM-DD");

      userRecordsToProcess.forEach((u) => {        
        
        (u.dates || []).forEach((day) => {
          const isToday = day.date === todayStr;
          const isPast = day.date < todayStr;

          (day.events || []).forEach((event, idx) => {
            const rawType = (event.eventType || "").toUpperCase();
            const rawStatus = (event.status || "").toLowerCase();
            const rawTitle = event.title || event.eventType || "";
            const rawTitleUpper = rawTitle.toUpperCase();
            const dataId = event.dataId;

            const checkInFormatted = formatTimeString(event.startTime);
            const checkOutFormatted = formatTimeString(event.endTime);
            const durationSec = calculateDurationInSeconds(
              event.startTime,
              event.endTime
            );
            const totalHours = durationSec / 3600;

            const hasStartTime = Boolean(
              event.startTime && event.startTime !== "00:00:00"
            );
            const hasEndTime = Boolean(
              event.endTime && event.endTime !== "00:00:00"
            );

            // Category determination
            let category = "present"; // present | regularize | pending_today | permission | leave | holiday | weekoff | other
            let isPendingToday = false;
            let chipBg = "#E6F7ED";
            let chipBorder = "#0F9D58";
            let chipText = "#0F9D58";
            let displayTitle = "";
            let subTitle = "";
            let description = "";
            let allDay = false;
            let start = day.date;
            let end = null;

            if (
              rawType === "HOLIDAY" ||
              rawTitleUpper.includes("HOLIDAY") ||
              rawStatus === "holiday"
            ) {
              category = "holiday";
              chipBg = "#F3E8FF";
              chipBorder = "#A855F7";
              chipText = "#7C3AED";
              displayTitle = formatText(rawTitle || "Holiday");
              subTitle = "Company Holiday";
              description = `Holiday: ${formatText(
                rawTitle || "Holiday"
              )} observed on ${day.date}.`;
              allDay = true;
              start = day.date;
            } else if (
              rawType.includes("WEEK") ||
              rawTitleUpper.includes("WEEK OFF") ||
              rawTitleUpper.includes("WEEKOFF") ||
              rawStatus === "weekoff"
            ) {
              category = "weekoff";
              chipBg = "#F3F4F6";
              chipBorder = "#D1D5DB";
              chipText = "#4B5563";
              displayTitle = "Week Off";
              subTitle = "Non-working Day";
              description = `Weekly off day (${moment(day.date).format(
                "dddd"
              )}).`;
              allDay = true;
              start = day.date;
            } else if (
              rawType.includes("LEAVE") ||
              rawStatus === "leave" ||
              rawTitleUpper.includes("LEAVE")
            ) {
              category = "leave";
              chipBg = "#FEF2F2";
              chipBorder = "#F87171";
              chipText = "#DC2626";
              displayTitle = formatText(rawTitle || "Leave");
              subTitle = `Status: ${formatText(event.status || "Approved")}`;
              description = `Leave record: ${formatText(
                rawTitle || "Leave"
              )} (${formatText(event.status || "Approved")}) on ${day.date}.`;
              allDay = !hasStartTime;
              start = hasStartTime ? `${day.date}T${event.startTime}` : day.date;
              end = hasEndTime ? `${day.date}T${event.endTime}` : null;
            } else if (
              rawType === "PERMISSION" ||
              rawTitleUpper.includes("PERMISSION")
            ) {
              category = "permission";
              chipBg = "#EFF6FF";
              chipBorder = "#3B82F6";
              chipText = "#1D4ED8";
              displayTitle = "Permission";
              subTitle =
                hasStartTime && hasEndTime
                  ? `${checkInFormatted} - ${checkOutFormatted}`
                  : `Permission (${formatText(event.status || "Pending")})`;
              description = `Permission requested from ${checkInFormatted} to ${checkOutFormatted}. Status: ${formatText(
                event.status || "Pending"
              )}.`;
              allDay = !hasStartTime;
              start = hasStartTime ? `${day.date}T${event.startTime}` : day.date;
              end = hasEndTime
                ? `${day.date}T${event.endTime}`
                : hasStartTime
                  ? `${day.date}T${moment(day.date + "T" + event.startTime)
                    .add(1, "hour")
                    .format("HH:mm:ss")}`
                  : null;
            } else if (
              rawType === "PRESENT" ||
              rawType === "ATTENDANCE" ||
              rawStatus === "present"
            ) {
              // 1. Today with check-in and NO check-out:
              // Keep an event saying Pending with blinking yellow dot and check-in time alone
              if (isToday && hasStartTime && !hasEndTime) {
                category = "pending_today";
                isPendingToday = true;
                chipBg = "#FEF9C3";
                chipBorder = "#EAB308";
                chipText = "#854D0E";
                displayTitle = "Pending";
                subTitle = `In: ${checkInFormatted}`;
                description = `Today's Attendance: Checked in at ${checkInFormatted}. Check-out is pending.`;
                allDay = false;
                start = `${day.date}T${event.startTime}`;
                end = `${day.date}T${moment().format("HH:mm:ss")}`;
              }
              // 2. Check-in and check-out difference >= 8 hours:
              // Full day Present
              else if (hasStartTime && hasEndTime && totalHours >= 8) {
                category = "present";
                chipBg = "#E6F7ED";
                chipBorder = "#5aa380";
                chipText = "#0F9D58";
                displayTitle = `In: ${checkInFormatted} - Out: ${checkOutFormatted}`;
                subTitle = `Duration: ${formatDuration(durationSec)}`;
                description = `Active Full Day: Checked in at ${checkInFormatted} and checked out at ${checkOutFormatted}. Total working duration: ${formatDuration(
                  durationSec
                )}.`;
                allDay = false;
                start = `${day.date}T${event.startTime}`;
                end = `${day.date}T${event.endTime}`;
              }
              // 3. Past day with check-in but NO check-out:
              // "To be Regularized" chip with only check-in data
              else if (isPast && hasStartTime && !hasEndTime) {
                category = "regularize";
                chipBg = "#FFF7ED";
                chipBorder = "#F97316";
                chipText = "#C2410C";
                displayTitle = "To be Regularized";
                subTitle = `In: ${checkInFormatted}`;
                description = `Incomplete Attendance: Checked in at ${checkInFormatted} with missing check-out. Regularization required.`;
                allDay = false;
                start = `${day.date}T${event.startTime}`;
                end = `${day.date}T${moment(day.date + "T" + event.startTime)
                  .add(1, "hour")
                  .format("HH:mm:ss")}`;
              }
              // 4. Check-in and check-out exist with duration < 8 hours on today or past:
              else if (
                hasStartTime &&
                hasEndTime &&
                totalHours < 8 &&
                (isToday || isPast)
              ) {
                category = "regularize";
                chipBg = "#FFF7ED";
                chipBorder = "#F97316";
                chipText = "#C2410C";
                displayTitle = "Less than 8 hrs";
                subTitle = `In: ${checkInFormatted} - Out: ${checkOutFormatted}`;
                description = `Less than 8 hours logged (${formatDuration(
                  durationSec
                )}). Checked in at ${checkInFormatted} and out at ${checkOutFormatted}. Attendance regularization recommended.`;
                allDay = false;
                start = `${day.date}T${event.startTime}`;
                end = `${day.date}T${event.endTime}`;
              }
              // 5. Present event without start/end times
              else {
                category = "present";
                chipBg = "#E6F7ED";
                chipBorder = "#5aa380";
                chipText = "#0F9D58";
                displayTitle = "Present";
                subTitle = formatText(event.status || "Present");
                description = `Attendance record: Present on ${day.date}.`;
                allDay = true;
                start = day.date;
              }
            } else {
              chipBg = "#F8FAFC";
              chipBorder = "#CBD5E1";
              chipText = "#334155";
              displayTitle = formatText(rawTitle || "Event");
              subTitle = event.status || "";
              description = `${formatText(rawTitle)} on ${day.date}.`;
              allDay = !hasStartTime;
              start = hasStartTime ? `${day.date}T${event.startTime}` : day.date;
              end = hasEndTime ? `${day.date}T${event.endTime}` : null;
            }



            parsedEvents.push({
              id: `${day.date}-${rawType}-${idx}`,
              title: displayTitle,
              start: start,
              end: end || undefined,
              allDay: allDay,
              type: category,
              rawEvent: event,
              dateStr: day.date,
              displayTitle,
              subTitle,
              description,
              chipBg,
              chipBorder,
              chipText,
              category,
              isPendingToday,
              eventStartTime: event.startTime,
              eventEndTime: event.endTime,
              checkIn: checkInFormatted,
              checkOut: checkOutFormatted,
              eventDuration: formatDuration(durationSec),
              dataId: dataId,
              status: event.status,
            });
          });
        });
      });

      setCalendarDataSet(parsedEvents);
    } catch (err) {
      console.error("Error fetching Calendar Data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Called whenever FullCalendar changes visible date range (month, week, day, today, next, prev)
  const handleDatesSet = useCallback((arg) => {
    const viewDate = arg.view.calendar.getDate();

    const m = viewDate.getMonth() + 1;
    const y = viewDate.getFullYear();

    // Skip if we already fetched for this month/year
    if (m === activeMonthRef.current.month && y === activeMonthRef.current.year) {
      return;
    }

    setCurrentMonth(m);
    setCurrentYear(y);

    fetchUserAttendanceDetails(m, y);
  }, []);



  // Filtered Events based on Active Filter
  const filteredEvents = useMemo(() => {
    if (!activeFilter) return calendarDataSet;
    if (activeFilter === "regularize") {
      return calendarDataSet.filter(
        (e) => e.category === "regularize" || e.category === "pending_today"
      );
    }
    return calendarDataSet.filter((e) => e.category === activeFilter);
  }, [calendarDataSet, activeFilter]);  
  
  // Selected date events & approval checks for the action dialog
  const selectedDateEvents = useMemo(() => {
    if (!selectedDate) return [];
    return calendarDataSet.filter((e) => e.dateStr === selectedDate);
  }, [calendarDataSet, selectedDate]);

  const isLeaveApprovedForSelectedDate = useMemo(() => {
    return selectedDateEvents.some(
      (e) =>
        e.category === "leave" &&
        (e.status?.toLowerCase() === "approved" ||
          e.rawEvent?.status?.toLowerCase() === "approved")
    );
  }, [selectedDateEvents]);

  const isPermissionApprovedForSelectedDate = useMemo(() => {
    return selectedDateEvents.some(
      (e) =>
        e.category === "permission" &&
        (e.status?.toLowerCase() === "approved" ||
          e.rawEvent?.status?.toLowerCase() === "approved")
    );
  }, [selectedDateEvents]);

  // Handle Event Click -> Show description popup directly next to the clicked event
  const handleEventClick = (info) => {
    info.jsEvent.preventDefault();
    info.jsEvent.stopPropagation();

    // Close FullCalendar's "+more" popover if open to prevent overlapping with description dialog
    const closeBtn = document.querySelector(".fc-more-popover .fc-popover-close");
    if (closeBtn) {
      closeBtn.click();
    } else {
      const popovers = document.querySelectorAll(".fc-more-popover, .fc-popover");
      popovers.forEach((p) => p.remove());
    }

    const eventProps = info.event.extendedProps;
    setSelectedDate(eventProps.dateStr || info.event.startStr);
    setSelectedEventData(eventProps);
    setPopoverAnchorEl(info.el);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
    setSelectedEventData(null);
  };

  // Date Click -> Open Action Dialog Box (Apply leave, Regularization, etc.) for Month view only
  const handleDateClick = (arg) => {
    if (arg.view.type === "dayGridMonth") {
      setSelectedDate(arg.dateStr);
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Open Leave Dialog from action dialog or popover
  const handleOpenLeave = () => {
    setLeaveDialogOpen(true);
    setDialogOpen(false);
    handleClosePopover();
  };

  const handleCloseLeave = () => {
    setLeaveDialogOpen(false);
    setDialogOpen(false);
  };

  // Open Regularization Dialog from action dialog or popover
  const handleOpenRegularization = (defaultInTime) => {
    if (defaultInTime) {
      setInTime(defaultInTime);
    }
    setRegularizationOpen(true);
    setDialogOpen(false);
    handleClosePopover();
  };

  const handleCloseRegularization = () => {
    setRegularizationOpen(false);
    setDialogOpen(false);
  };

  // Delete pending leave request
  const handleDeleteLeave = (targetLeaveId) => {
    const idToDelete = targetLeaveId || selectedEventData?.dataId;
    if (!idToDelete) {
      Swal.fire({
        icon: "warning",
        title: "Unable to delete",
        text: "Leave identifier not found.",
      });
      return;
    }

    Swal.fire({
      title: "Delete Leave Request",
      text: "Are you sure you want to delete this pending leave request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#64748B",
      confirmButtonText: "Yes, Delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const deleteQuery = `
            mutation delete($id: String!) {
              DeleteLeaveRequest(id: $id) {
                success
                message
              }
            }
          `;
          const response = await axios.post(BASEURL, {
            query: deleteQuery,
            variables: { id: String(idToDelete) },
          });

          if (response.data?.data?.DeleteLeaveRequest?.success) {
            Swal.fire({
              position: "center",
              icon: "success",
              title: "Leave Deleted Successfully!",
              showConfirmButton: false,
              timer: 2000,
            });
            handleClosePopover();
            fetchUserAttendanceDetails(currentMonth, currentYear);
          } else {
            Swal.fire({
              icon: "error",
              title: "Failed to delete leave",
              text:
                response.data?.data?.DeleteLeaveRequest?.message ||
                "An unexpected error occurred.",
            });
          }
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: err.message || "Failed to delete leave request.",
          });
        }
      }
    });
  };

  // Cancel approved leave request
  const handleCancelLeave = (targetLeaveId) => {
    const idToCancel = targetLeaveId || selectedEventData?.dataId;
    if (!idToCancel) {
      Swal.fire({
        icon: "warning",
        title: "Unable to cancel",
        text: "Leave identifier not found.",
      });
      return;
    }

    Swal.fire({
      title: "Confirmation",
      text: "Are you sure you want to cancel your Leave?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#64748B",
      confirmButtonText: "Yes, Cancel Leave",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const cancelQuery = `
            mutation cancelleaverequest($id: ID!) {
              CancelLeaveRequest(id: $id) {
                success
                message
                error
              }
            }
          `;
          const response = await axios.post(BASEURL, {
            query: cancelQuery,
            variables: { id: String(idToCancel) },
          });

          const resData = response.data?.data?.CancelLeaveRequest;
          if (resData?.success) {
            Swal.fire({
              position: "center",
              icon: "success",
              title: "Cancel successfully",
              showConfirmButton: false,
              timer: 2500,
            });
            handleClosePopover();
            fetchUserAttendanceDetails(currentMonth, currentYear);
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: resData?.message || "Failed to cancel leave.",
            });
          }
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: err.message || "Failed to cancel leave.",
          });
        }
      }
    });
  };

  // Render Category Icon for Popover and Chips
  const renderCategoryIcon = (category) => {
    switch (category) {
      case "present":
        return <CheckCircleOutline sx={{ fontSize: 18, color: "#0F9D58" }} />;
      case "regularize":
        return <EventRepeat sx={{ fontSize: 18, color: "#F97316" }} />;
      case "pending_today":
        return <AccessTime sx={{ fontSize: 18, color: "#EAB308" }} />;
      case "permission":
        return <TimerOutlined sx={{ fontSize: 18, color: "#2563EB" }} />;
      case "leave":
        return <EventBusy sx={{ fontSize: 18, color: "#DC2626" }} />;
      case "holiday":
        return <BeachAccessOutlined sx={{ fontSize: 18, color: "#7C3AED" }} />;
      case "weekoff":
        return <WeekendOutlined sx={{ fontSize: 18, color: "#6B7280" }} />;
      default:
        return <InfoOutlined sx={{ fontSize: 18, color: "#3B82F6" }} />;
    }
  };

  return (
    <>
      {/* Apply Leave Form */}
      <ApplyLeaveDialog
        leaveId={leaveId}
        viewScreen={viewScreen}
        onClose={handleCloseLeave}
        leaveDialogOpen={leaveDialogOpen}
        setLeaveDialogOpen={setLeaveDialogOpen}
        handleOpenLeave={handleOpenLeave}
        handleCloseLeave={handleCloseLeave}
        selectedDate={selectedDate}
      />

      {/* Regularization Form */}
      <RegularizationForm
        inTime={inTime}
        setInTime={setInTime}
        regularizationOpen={regularizationOpen}
        setRegularizationOpen={setRegularizationOpen}
        handleOpenRegularization={() => handleOpenRegularization()}
        handleCloseRegularization={handleCloseRegularization}
        selectedDate={selectedDate}
      />

      <DashboardLayout>
        <CalendarActions
          selectedDate={selectedDate}
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          onClose={handleDialogClose}
          isLeaveApproved={isLeaveApprovedForSelectedDate}
          isPermissionApproved={isPermissionApprovedForSelectedDate}
        />

        <Card>
          <CardHeader
            title={
              <MaterialBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <MaterialTypography
                  variant="h5"
                  fontWeight="medium"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  Employee Calendar
                </MaterialTypography>

                <MaterialBox
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                >
                  {/* Legend Chips Filter */}
                  <MaterialBox display="flex" gap={0.8} flexWrap="wrap">
                    <Chip
                      icon={<EventAvailable />}
                      label="Present (≥8h)"
                      variant={
                        activeFilter === "present" ? "filled" : "outlined"
                      }
                      size="small"
                      sx={{
                        color: "#0F9D58",
                        borderColor: "#0F9D58",
                        backgroundColor:
                          activeFilter === "present"
                            ? "#E6F7ED"
                            : "transparent",
                        "& .MuiChip-icon": { color: "#0F9D58" },
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setActiveFilter(
                          activeFilter === "present" ? null : "present"
                        )
                      }
                    />

                    <Chip
                      icon={<EventRepeat />}
                      label="To be Regularized"
                      variant={
                        activeFilter === "regularize" ? "filled" : "outlined"
                      }
                      size="small"
                      sx={{
                        color: "#EA580C",
                        borderColor: "#F97316",
                        backgroundColor:
                          activeFilter === "regularize"
                            ? "#FFF7ED"
                            : "transparent",
                        "& .MuiChip-icon": { color: "#EA580C" },
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setActiveFilter(
                          activeFilter === "regularize" ? null : "regularize"
                        )
                      }
                    />

                    <Chip
                      icon={<EventBusy />}
                      label="Leave"
                      variant={activeFilter === "leave" ? "filled" : "outlined"}
                      size="small"
                      sx={{
                        color: "#DC2626",
                        borderColor: "#DC2626",
                        backgroundColor:
                          activeFilter === "leave" ? "#FEF2F2" : "transparent",
                        "& .MuiChip-icon": { color: "#DC2626" },
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setActiveFilter(
                          activeFilter === "leave" ? null : "leave"
                        )
                      }
                    />

                    <Chip
                      icon={<TimerOutlined />}
                      label="Permission"
                      variant={
                        activeFilter === "permission" ? "filled" : "outlined"
                      }
                      size="small"
                      sx={{
                        color: "#2563EB",
                        borderColor: "#3B82F6",
                        backgroundColor:
                          activeFilter === "permission"
                            ? "#EFF6FF"
                            : "transparent",
                        "& .MuiChip-icon": { color: "#2563EB" },
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setActiveFilter(
                          activeFilter === "permission" ? null : "permission"
                        )
                      }
                    />

                    <Chip
                      icon={<BeachAccessOutlined />}
                      label="Holiday"
                      variant={
                        activeFilter === "holiday" ? "filled" : "outlined"
                      }
                      size="small"
                      sx={{
                        color: "#7C3AED",
                        borderColor: "#7C3AED",
                        backgroundColor:
                          activeFilter === "holiday"
                            ? "#F3E8FF"
                            : "transparent",
                        "& .MuiChip-icon": { color: "#7C3AED" },
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setActiveFilter(
                          activeFilter === "holiday" ? null : "holiday"
                        )
                      }
                    />

                    <Chip
                      icon={<WeekendOutlined />}
                      label="Week Off"
                      variant={
                        activeFilter === "weekoff" ? "filled" : "outlined"
                      }
                      size="small"
                      sx={{
                        color: "#4B5563",
                        borderColor: "#9CA3AF",
                        backgroundColor:
                          activeFilter === "weekoff"
                            ? "#F3F4F6"
                            : "transparent",
                        "& .MuiChip-icon": { color: "#6B7280" },
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setActiveFilter(
                          activeFilter === "weekoff" ? null : "weekoff"
                        )
                      }
                    />
                  </MaterialBox>

                  {/* Refresh Button */}
                  <Tooltip
                    disableInteractive
                    enterDelay={500}
                    title="Reset Filter / Refresh"
                  >
                    <IconButton
                      size="small"
                      sx={{ color: "#0B1D51" }}
                      onClick={() => {
                        setActiveFilter(null);
                        fetchUserAttendanceDetails(currentMonth, currentYear);
                      }}
                    >
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </MaterialBox>
              </MaterialBox>
            }
          />

          <CardContent className="calendar" sx={{ position: "relative" }}>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(2px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 4,
                }}
              >
                <CircularProgress size={36} sx={{ color: "#0B1D51" }} />
              </Box>
            )}
            <FullCalendar
              ref={calendarRef}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                listPlugin,
                interactionPlugin,
              ]}
              initialView="dayGridMonth"
              weekends={true}
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              datesSet={handleDatesSet}
              contentHeight="auto"
              fixedWeekCount={false}
              dayMaxEvents={3}
              slotEventOverlap={true}
              eventMinHeight={26}
              slotDuration="00:30:00"
              slotMinTime="06:00:00"
              slotMaxTime="23:00:00"
              allDaySlot={true}
              slotLabelFormat={{
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              }}
              buttonText={{
                today: "Today",
                month: "Month",
                week: "Week",
                day: "Day",
                list: "List",
              }}
              events={filteredEvents}
              eventContent={(arg) => {
                const data = arg.event.extendedProps;

                // Today's pending check-in with blinking yellow dot and check-in time alone
                if (data.isPendingToday) {
                  return (
                    <div
                      className="calendar-event-chip"
                      style={{
                        backgroundColor: "#FEF9C3",
                        border: "1px solid #EAB308",
                        color: "#854D0E",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          fontWeight: 700,
                          fontSize: "11px",
                          lineHeight: "1.2",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <span>Pending</span>
                        <span className="blinking-yellow-dot" />
                      </div>
                      {data.subTitle && (
                        <div
                          style={{
                            fontSize: "10px",
                            opacity: 0.9,
                            marginTop: "2px",
                            lineHeight: "1.1",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {data.subTitle}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div
                    className="calendar-event-chip"
                    style={{
                      backgroundColor: data.chipBg || "#F1F5F9",
                      border: `1px solid ${data.chipBorder || "#CBD5E1"}`,
                      color: data.chipText || "#1E293B",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "11px",
                        lineHeight: "1.2",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {data.displayTitle || arg.event.title}
                    </div>
                    {data.subTitle && (
                      <div
                        style={{
                          fontSize: "10px",
                          opacity: 0.85,
                          marginTop: "2px",
                          lineHeight: "1.1",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {data.subTitle}
                      </div>
                    )}
                  </div>
                );
              }}
              headerToolbar={{
                left: "prev,today,next",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
              }}
            />
          </CardContent>
        </Card>

        {/* Small Event Description Popover right next to the clicked event */}
        <Popover
          open={Boolean(popoverAnchorEl && selectedEventData)}
          anchorEl={popoverAnchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              p: 2,
              zIndex: 10,
              backgroundColor: "#fff",
              width: 300,
              maxWidth: "90vw",
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
              border: `1px solid ${selectedEventData?.chipBorder || "#E2E8F0"}`,
            },
          }}
        >
          {selectedEventData && (
            <Box display="flex" flexDirection="column" gap={1.2}>
              {/* Header with Icon and Title */}
              <Box display="flex" alignItems="center" gap={1}>
                {renderCategoryIcon(selectedEventData.category)}
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: selectedEventData.chipText || "#0F172A",
                    fontSize: "0.95rem",
                  }}
                >
                  {selectedEventData.displayTitle}
                </Typography>
              </Box>

              <Divider sx={{ my: 0.5 }} />

              {/* Date information */}
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTime sx={{ fontSize: 16, color: "#64748B" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#475569", fontWeight: 600 }}
                >
                  Date:{" "}
                  {moment(selectedEventData.dateStr).format(
                    "DD MMM YYYY (dddd)"
                  )}
                </Typography>
              </Box>

              {/* Attendance specific info if checkIn is available */}
              {selectedEventData.checkIn &&
                selectedEventData.checkIn !== "N/A" && (
                  <Box
                    sx={{
                      backgroundColor: "#F8FAFC",
                      p: 1,
                      borderRadius: "6px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#334155" }}>
                      <strong>In Time:</strong> {selectedEventData.checkIn}
                    </Typography>
                    {selectedEventData.checkOut &&
                      selectedEventData.checkOut !== "N/A" && (
                        <Typography variant="caption" sx={{ color: "#334155" }}>
                          <strong>Out Time:</strong> {selectedEventData.checkOut}
                        </Typography>
                      )}
                    {selectedEventData.eventDuration &&
                      selectedEventData.eventDuration !== "0 hrs" && (
                        <Typography variant="caption" sx={{ color: "#334155" }}>
                          <strong>Logged Duration:</strong>{" "}
                          {selectedEventData.eventDuration}
                        </Typography>
                      )}
                  </Box>
                )}

              {/* Description Body */}
              <Typography
                variant="body2"
                sx={{
                  color: "#475569",
                  fontSize: "0.8rem",
                  lineHeight: 1.4,
                  mt: 0.5,
                }}
              >
                {selectedEventData.description}
              </Typography>

              {/* Quick Action Button for Regularization / Leave */}
              {selectedEventData.category === "regularize" && (
                <Box mt={1} display="flex" justifyContent="flex-end">
                  <Chip
                    label="Regularize Now"
                    size="small"
                    color="warning"
                    clickable
                    onClick={() =>
                      handleOpenRegularization(
                        selectedEventData.eventStartTime?.slice(0, 5)
                      )
                    }
                    sx={{ fontWeight: 600, fontSize: "11px" }}
                  />
                </Box>
              )}

              {selectedEventData.category === "leave" && (
                <Box mt={1} display="flex" justifyContent="flex-end" gap={1}>
                  {selectedEventData.status?.toLowerCase() === "pending" ? (
                    <Chip
                      icon={
                        <DeleteOutline
                          sx={{ fontSize: "16px !important", color: "#DC2626" }}
                        />
                      }
                      label="Delete Leave"
                      size="small"
                      color="error"
                      variant="outlined"
                      clickable
                      onClick={() =>
                        handleDeleteLeave(selectedEventData.dataId)
                      }
                      sx={{ fontWeight: 600, fontSize: "11px" }}
                    />
                  ) : selectedEventData.status?.toLowerCase() === "approved" ? (
                    <Chip
                      label="Cancel Leave"
                      size="small"
                      color="error"
                      variant="contained"
                      clickable
                      onClick={() =>
                        handleCancelLeave(selectedEventData.dataId)
                      }
                      sx={{
                        fontWeight: 600,
                        fontSize: "11px",
                        backgroundColor: "#DC2626",
                        color: "#fff",
                      }}
                    />
                  ) : (
                    <Chip
                      label="Apply Leave"
                      size="small"
                      color="error"
                      clickable
                      onClick={handleOpenLeave}
                      sx={{ fontWeight: 600, fontSize: "11px" }}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </Popover>
      </DashboardLayout>
    </>
  );
};

export default Calendar;